/**
 * BayReviews.ScriptLoader
 *
 * Shared loader for the BayEngage/TargetBay reviews widget bundle.
 *
 * This module centralizes all tbr-bundle.min.js injection logic so that the
 * PDP view and the footer view do not race each other. It coalesces multiple
 * load requests, polls for placeholder DOM nodes, waits for the standard
 * 2000ms SCA settle delay, then injects the bundle once per page view.
 */

define('BayReviews.ScriptLoader', ['jQuery'], function (jQuery) {
    'use strict';

    var loader = {
        scriptId: 'bayengage-reviews-script',

        placeholderIds: [
            '_tbr_stat',
            '_tbr_rev-sum',
            '_tbr_pdr',
            '_tbr_qna',
            'bayengage-reviews-widget',
            'targetbay_reviews',
            '_tbr_bus',
            '_tbr_endrsmtc',
            '_tbr_sptltc',
            '_tbr_scrol',
            '_tbr_fomop',
            '_tbr_bdg',
            '_tbr_col_stat'
        ],

        placeholderClasses: [
            '.bayengage-embed-form'
        ],

        debounceMs: 50,
        pollMs: 50,
        pollMaxAttempts: 100,
        injectDelayMs: 2000,

        checkExistInterval: null,
        renderTimeout: null,
        debounceTimeout: null,
        lastPayload: null,
        activeContainerId: null,
        debugMode: false,

        /**
         * Conditionally log to the console when debug mode is enabled.
         */
        log: function (message, type) {
            if (!this.debugMode || !window.console) {
                return;
            }
            var fn = window.console[type || 'log'];
            if (typeof fn === 'function') {
                fn('BayReviews: ' + message);
            }
        },

        /**
         * Clear all pending timers and reset injection state.
         */
        reset: function () {
            if (this.checkExistInterval) {
                clearInterval(this.checkExistInterval);
                this.checkExistInterval = null;
            }
            if (this.renderTimeout) {
                clearTimeout(this.renderTimeout);
                this.renderTimeout = null;
            }
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
                this.debounceTimeout = null;
            }
        },

        /**
         * Request a bundle load. Multiple calls within the debounce window are
         * merged into a single injection.
         */
        load: function (payload, options) {
            options = options || {};
            this.debugMode = !!options.debugMode;
            if (options.containerId) {
                this.activeContainerId = options.containerId;
            }
            this.lastPayload = this.mergePayload(this.lastPayload, payload);

            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }

            var self = this;
            this.debounceTimeout = setTimeout(function () {
                self.performLoad();
            }, this.debounceMs);
        },

        /**
         * Merge a new payload into the pending payload. commonData and
         * tbrConfig are shallow-merged so later values win.
         */
        mergePayload: function (existing, incoming) {
            if (!existing) {
                return jQuery.extend(true, {}, incoming);
            }
            if (!incoming) {
                return existing;
            }

            var merged = jQuery.extend(true, {}, existing);
            merged.commonData = jQuery.extend({}, merged.commonData, incoming.commonData);
            merged.tbrConfig = jQuery.extend({}, merged.tbrConfig, incoming.tbrConfig);
            if (incoming.bundleSrc) {
                merged.bundleSrc = incoming.bundleSrc;
            }
            return merged;
        },

        /**
         * Actual load routine: poll for placeholders, wait 2s, then inject.
         */
        performLoad: function () {
            this.reset();

            var self = this;
            var attempts = 0;

            this.log('Polling for BayReviews placeholders...');

            this.checkExistInterval = setInterval(function () {
                if (++attempts > self.pollMaxAttempts) {
                    self.log('Placeholder polling timed out after ' + (self.pollMaxAttempts * self.pollMs) + 'ms', 'warn');
                    clearInterval(self.checkExistInterval);
                    self.checkExistInterval = null;
                    return;
                }

                var foundId = null;
                var idsToCheck = self.placeholderIds.slice();
                if (self.activeContainerId) {
                    idsToCheck.push(self.activeContainerId);
                }
                for (var i = 0; i < idsToCheck.length; i++) {
                    if (document.getElementById(idsToCheck[i])) {
                        foundId = idsToCheck[i];
                        break;
                    }
                }

                if (!foundId && self.placeholderClasses && self.placeholderClasses.length) {
                    for (var c = 0; c < self.placeholderClasses.length; c++) {
                        if (document.querySelector(self.placeholderClasses[c])) {
                            foundId = self.placeholderClasses[c];
                            break;
                        }
                    }
                }

                if (!foundId) {
                    return;
                }

                clearInterval(self.checkExistInterval);
                self.checkExistInterval = null;

                self.log('Placeholder found: ' + foundId + '. Scheduling injection in ' + self.injectDelayMs + 'ms');

                self.renderTimeout = setTimeout(function () {
                    self.injectScript(self.lastPayload);
                }, self.injectDelayMs);
            }, this.pollMs);
        },

        /**
         * Update window.brv.commonData and window.tbrConfig without injecting
         * the bundle again. Used when async data (user profile, matrix variant)
         * arrives after the script is already on the page.
         */
        updateGlobals: function (payload) {
            payload = payload || this.lastPayload;
            if (!payload) {
                return;
            }

            this.lastPayload = this.mergePayload(this.lastPayload, payload);

            window.brv = window.brv || {};
            window.brv.commonData = jQuery.extend({}, window.brv.commonData || {}, this.lastPayload.commonData);
            window.tbrConfig = this.lastPayload.tbrConfig;

            this.log('Global data updated');
        },

        /**
         * Update data-product-id attributes on all known PDP placeholders.
         */
        updateProductAttributes: function (productId) {
            if (!productId) {
                return;
            }

            var ids = ['_tbr_stat', '_tbr_rev-sum', '_tbr_pdr', '_tbr_qna', 'bayengage-reviews-widget', 'targetbay_reviews'];
            if (this.activeContainerId) {
                ids.push(this.activeContainerId);
            }
            for (var i = 0; i < ids.length; i++) {
                var el = document.getElementById(ids[i]);
                if (el) {
                    el.setAttribute('data-product-id', String(productId));
                }
            }
        },

        /**
         * Inject the bundle script tag. Always removes any previous tag first
         * so the widget re-initializes correctly on SPA navigation.
         */
        injectScript: function (payload) {
            if (!payload || !payload.bundleSrc) {
                this.log('No bundleSrc provided, skipping injection.', 'error');
                return;
            }

            this.updateGlobals(payload);

            var existingScript = document.getElementById(this.scriptId);
            if (existingScript && existingScript.parentNode) {
                existingScript.parentNode.removeChild(existingScript);
            }

            var url = payload.bundleSrc;
            url += (url.indexOf('?') >= 0 ? '&' : '?') + '_=' + new Date().getTime();

            var self = this;
            var script = document.createElement('script');
            script.id = this.scriptId;
            script.src = url;
            script.type = 'text/javascript';
            script.async = true;

            // NetSuite/SuiteCommerce uses CSP nonces on script tags. Copy the
            // nonce from an existing script so our dynamically injected bundle
            // is not blocked by the browser.
            var nonceSource = document.querySelector('script[nonce]');
            var nonce = nonceSource ? nonceSource.getAttribute('nonce') : '';
            if (nonce) {
                script.setAttribute('nonce', nonce);
            }

            script.onload = function () {
                script.setAttribute('data-loaded', 'true');
                self.log('Bundle loaded: ' + url);
                if (window.CustomEvent && typeof window.dispatchEvent === 'function') {
                    window.dispatchEvent(new window.CustomEvent('bayreviews:bundleLoaded'));
                }
            };

            script.onerror = function () {
                self.log('Failed to load bundle: ' + url, 'error');
            };

            var firstScript = document.scripts && document.scripts[0];
            if (firstScript && firstScript.parentNode) {
                firstScript.parentNode.insertBefore(script, firstScript);
            } else {
                document.head.appendChild(script);
            }

            this.log('Injected bundle: ' + url);
        }
    };

    return loader;
});
